/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";

import { memo, useState, useEffect, useCallback } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { useNavigate } from "react-router-dom";

import ReactLoading from 'react-loading';

// コンポーネント
import H3 from "components/parts/title/H3";
import UploadedPhoto from "components/parts/image/ImageConfirm/UploadedPhoto";
import NextButton from "components/ui/button/NextButton";
import BackButton from "components/ui/button/BackButton";
import Card from "components/ui/card/Card";
import BackToInputLink from "components/ui/link/BackToInputLink";
import CautionNormalRed from "components/ui/caution/CautionNormalRed";

// CSS
import { mtgLabel, mtgBodyNoMb, mtgSection } from "assets/css/stylesCss";
import { whiteSpacePreWrap, loadingCss, mtgConfText, mtgConfLabel } from "assets/css/shchMtmr.js";

// JS
import { imgFlex } from "assets/css/shchMtmr.js";
import { formatSelectKiki, formatCustNm, formatYubinNo, formatJusyo, chgChkValToStr, formatTelNumber } from "utilities/formater";
import {formatYmd, getWeekDayNm, chgStringToDate, isDisabledCalendarCell} from "utilities/date";
import { chgEmptyCharToNull, getTokenName, isEmpty, formatBantGo } from "utilities/string";

// 入力フォームのstore保存
import { useStateMachine } from "little-state-machine";
import {getStore, confirmAction, appointmentAction} from "stores/actions.js";

// Apollo Clientと接続
import useCreateMutation from "hooks/useCreateMutation";
import {CREATE_SHCH_MTMR, GET_CAPACITY_INFO} from "graphql/query";
import useGetLazyQuery from "../hooks/useGetLazyQuery";

/**
 * 内容確認
 */
const Conf = memo(() => {

  // 登録処理を実施するフック
  const [createShchMtmr, loading] = useCreateMutation(CREATE_SHCH_MTMR);
  // 住所コードから担当店舗の余力を取得
  const [getCapacityInfo, loadingCapacity] = useGetLazyQuery(GET_CAPACITY_INFO, "no-cache");

  const [accepted, setAccepted] = useState(false);
  const [invalidDate, setInvalidDate] = useState(false);

  // 画面遷移用
  const navigate = useNavigate();

  // store取得・更新
  const { state } = useStateMachine(getStore);
  const { actions } = useStateMachine({ confirmAction,appointmentAction });

  // ご希望日時(Date型)
  const dateAppointmentYmd = chgStringToDate(state.storeAppointment.appointmentYmd + "0000");

  // 初期読み込み
  useEffect(() => {
    // 既に受付完了済みの場合エラーメッセージ表示
    if (state.storeConfirm.uktkNo !== "" && state.storeConfirm.uktkNo !== null) {
      setAccepted(true);
    } else {
      let appointmentYmd = state.storeAppointment.appointmentYmd;
      let kbuHmDay1 = state.storeAppointment.appointmentHmTimeCd;
      let addressKj = state.storeAppointment.addressKj;

      // 当日の時間帯余力チェック
      if (isDisabledCalendarCell(appointmentYmd, kbuHmDay1) ) {
        window.scrollTo(0, 0);
        return setInvalidDate(true);
      }
      // 余力の存在チェック
      existsCapacityInfo(appointmentYmd, kbuHmDay1, addressKj);
    }
  }, []);

  const methods = useForm({
    defaultValues: {
      isActive: true,
      contact: state.storeContract.contact,
      guidanceAgree: state.storeContract.guidanceAgree,
      isDispMtgSlct: state.storeContract.isDispMtgSlct,
      isDispMtgBtn: state.storeContract.isDispMtgBtn,
      mtgLoginSelect: state.storeContract.mtgLoginSelect,
    },
    mode: "onSubmit",
    reValidateMode: "onSubmit",
  });

  const {
    formState: { isValid },
  } = methods;

  /** 完了画面へ遷移 */
  const onSubmit = (data) => {
    register(data);
  };

  // 登録処理
  const register = useCallback((data) => {
    // 当日の時間帯チェック
    if (isDisabledCalendarCell(state.storeAppointment.appointmentYmd, state.storeAppointment.appointmentHmTimeCd)) {
      // 選択予定枠が時間帯チェックでエラーの場合日付選択画面へ遷移
      navigate("/appointmentCalendar", {
        state: { isSubmit: true, noCapacity: true },
      });
      return;
    }

    // 登録用データを生成
    const regData = createData(state, data);
    createShchMtmr(regData,
      (res) => {
        let err = res.resultStatus;

        // 正常に登録が完了した場合
        if (err === 1) {
          actions.confirmAction(res);
          // トークンを破棄
          sessionStorage.removeItem("token");
          // 申込完了ページへ遷移
          navigate("/thanks", {
            state: { isSubmit: true },
          });
        } else　if (err === 2) {
          // 余力なしの場合カレンダー画面へ遷移
          navigate("/appointmentCalendar", {
            state: { isSubmit: true, noCapacity: true },
          });
        } else {
          if(res.errorResponse && res.errorResponse.errorCd === 500) {
            // 500エラーページへ遷移
            navigate("/error", {
              state: { errorCode: 500 },
            });
            return;
          }
          // 認証エラーエラーページへ遷移
          navigate("/error", {
            state: { errorCode: 401 },
          });
        }
      },
      // エラーページへ遷移
      (e) => {
        navigate("/error", {
          state: { errorCode: 500 },
        });
      }
    );
  }, []);

  /**
   * 選択日時の余力が存在するか確認し、ボタンの活性非活性を切り替える
   * 余力がある場合:true
   * 余力がない場合:false
   * */
  const existsCapacityInfo = async (kbuHmDay, kbuHmDay1, addressKj) => {
    let errorCode = "";
    // 指定日付の余力を取得
    await getCapacityInfo({
      input: {
        startYmd: kbuHmDay,
        sgyskAddrCd: addressKj,
        endYmd: kbuHmDay
      },
    }, (data) => {
      // エラー存在チェック
      let err = data.errorResponse;
      let chkCapacity = false;
      if(!err) {
        if (kbuHmDay1 === "01") {
          // 指定枠が「01:09:00〜」の場合、
          if (data.capacityList[0].hasCapacityAM === 1) chkCapacity = true;
        } else if (kbuHmDay1 === "02") {
          // 指定枠が「02:13:00〜」の場合、
          if (data.capacityList[0].hasCapacity1315 === 1) chkCapacity = true;
        } else if (kbuHmDay1 === "03") {
          // 指定枠が「03:15:00〜」の場合、
          if (data.capacityList[0].hasCapacity1517 === 1) chkCapacity = true;
        }
      } else {
        // エラー
        console.log("errorCd:" + err.errorCd + " " + err.errorMsg)
        // 担当店が取得できない場合
        // if (data.isOutOfArea) {
          // setIsOutOfServiceArea(true);
          errorCode = "408";
          return;
        // }
      }
      // 余力なしの枠が指定されている場合、バリデーションをtrue
      if (!chkCapacity) setInvalidDate(true);
    }, (e) => {
      if ((e.message === "Response not successful: Received status code 500") ||
          (e.message === "Cannot convert undefined or null to object")){
        // 余力問い合わせでエラーの場合ネットワークエラー画面へ
        errorCode = "408";
        console.log("errorCd:" + e.errorCd + " " + e.errorMsg)
        return;
      }
    }, false);
    // エラーページへ遷移
    if (errorCode !== "") {
      navigate("/error", {
        state: { errorCode: errorCode },
      });
    }
  };

  return (
    <>
      <h1>内容確認</h1>
      <FormProvider {...methods}>

        {/** 受付済みの場合メッセージ表示 */}
        {accepted && (
          <CautionNormalRed>既にお申し込み済みの為、お申し込みできませんでした。メールをご確認頂くようお願いいたします。</CautionNormalRed>
        )}

        {/** ご希望日時再選択依頼メッセージ表示 */}
        {invalidDate && (
          <CautionNormalRed>ご指定のご希望日時ではお申し込みいただけません。再度、ご希望日時を選択してください。</CautionNormalRed>
        )}

        {/* お申込み条件確認 */}
        <form onSubmit={methods.handleSubmit(onSubmit)}>

          {/** ご希望日時 */}
          <H3 title="ご希望日時" required={true}></H3>
          <Card>
            {/** ご訪問日時 */}
            <div>
              <p css={mtgConfText}>
                {formatYmd(state.storeAppointment.appointmentYmd) + " "
                  + "（" + getWeekDayNm(state.storeAppointment.appointmentYmd) + "）"
                  + getHmTimeCdName(state.storeAppointment.appointmentHmTimeCd)}
              </p>
              <hr css={mtgSection} />
            </div>

            {/* 戻るリンク */}
            <div>
              <BackToInputLink path="/appointmentCalendar">
                ご希望日時を修正する
              </BackToInputLink>
            </div>
          </Card>

          {/** 機器選択 */}
          <H3 title="機器選択" required={true}></H3>
          <Card>
            {/** 見積したい機器 */}
            <div>
              <label css={mtgConfLabel}>見積もり依頼する機器</label>
              <p css={mtgConfText}>{formatSelectKiki(state.storeKikiSelect.selectKikiNm)}</p>
              <hr css={mtgSection} />
            </div>

            {/** ご要望 */}
            <div>
              <label css={mtgConfLabel}>ご要望</label>
              <p css={[mtgConfText, whiteSpacePreWrap]}>{state.storeKikiSelect.request}</p>
              <hr css={mtgSection} />
            </div>

            {/** 写真データ */}
            <div>
              {/** MEMO:通常のmtgBodyとmarginBottomが違うため直書き */}
              <label css={mtgLabel} style={{ marginBottom: "16px" }}>写真データ</label>
              {state.storeKikiSelect.tmpImage1 !== "" && (
                <div css={imgFlex}>
                  <UploadedPhoto
                    uploadImgSmall={state.storeKikiSelect.tmpImage1}
                  ></UploadedPhoto>
                  {state.storeKikiSelect.tmpImage2 !== "" && (
                    <UploadedPhoto
                      uploadImgSmall={state.storeKikiSelect.tmpImage2}
                    ></UploadedPhoto>
                  )}
                  {state.storeKikiSelect.tmpImage3 !== "" && (
                    <UploadedPhoto
                      uploadImgSmall={state.storeKikiSelect.tmpImage3}
                    ></UploadedPhoto>
                  )}
                </div>
              )}
              <hr css={mtgSection} />
            </div>

            {/** 戻るリンク */}
            <div>
              <BackToInputLink path="/kikiSelect">
                見積もり依頼する機器を修正する
              </BackToInputLink>
            </div>
          </Card>

          {/** お客さま情報 */}
          <H3 title="お客さま情報" required={true}></H3>
          <Card>

            {/** メールアドレス */}
            <div>
              <label css={mtgConfLabel}>メールアドレス</label>
              <p css={mtgConfText}>{state.storeContract.mailaddress}</p>
              <hr css={mtgSection} />
            </div>

            {/** お名前 */}
            <div>
              <label css={mtgConfLabel}>お名前</label>
              <p css={mtgConfText}>{
                formatCustNm(state.storeContract.mskmSei, state.storeContract.mskmMei) + "／"
                + formatCustNm(state.storeContract.mskmSeiKn, state.storeContract.mskmMeiKn)}</p>
              <hr css={mtgSection} />
            </div>

            {/** 携帯電話番号 */}
            <div>
              <label css={mtgConfLabel}>携帯電話番号</label>
              <p css={mtgConfText}>{formatTelNumber(state.storeContract.phoneNo)}</p>
              <hr css={mtgSection} />
            </div>

            {/** 固定電話番号 */}
            <div>
              <label css={mtgConfLabel}>固定電話番号</label>
              <p css={mtgConfText}>{formatTelNumber(state.storeContract.telNo)}</p>
              <hr css={mtgSection} />
            </div>

            {/** ご住所（設置・交換作業場所） */}
            <div>
              <label css={mtgConfLabel}>ご住所（設置・交換作業場所）</label>
              <p css={mtgBodyNoMb}>
                {formatYubinNo(state.storeAppointment.yubinNo, true)}
                <br />
                {formatJusyo(
                  state.storeAppointment.addressKjLabel,
                  state.storeContract.bant,
                  state.storeContract.go,
                  state.storeContract.bldNmKj,
                  state.storeContract.muneban,
                  state.storeContract.heyaban
                )}
              </p>
              <hr css={mtgSection} />
            </div>

            {/** ご連絡事項など */}
            <div>
              <label css={mtgConfLabel}>ご連絡事項など</label>
              <p css={[mtgConfText, whiteSpacePreWrap]}>{state.storeContract.contact}</p>
              <hr css={mtgSection} />
            </div>

            {/** メールでのご案内 */}
            <div>
              <label css={mtgConfLabel}>メールでのご案内</label>
              <p css={mtgConfText}>{chgChkValToStr(state.storeContract.guidanceAgree)}</p>
              <hr css={mtgSection} />
            </div>

            {/** 戻るリンク */}
            <div>
              <BackToInputLink path="/contract">
                お客さま情報を修正する
              </BackToInputLink>
            </div>
          </Card>

          {/** この内容で申し込む */}
          <NextButton
            isActive={isValid}
            value={isValid ? "この内容で申し込む" : ""}
            isAccepted={accepted}
            isInvalidDate={invalidDate}
            isLoading={(loadingCapacity || loading)}
          ></NextButton>
        </form>

        {/** 戻るボタン */}
        <BackButton url={"/Contract"} />
      </FormProvider>

      {/** ローディング */}
      {(loadingCapacity || loading) && (
        <div css={[loadingCss]}>
          <ReactLoading type="spin" color="#b5b5b5" height="50px" width="50px" />
        </div>
      )}
    </>
  );
});

/**
 * 訪問時間コードから訪問時間名称を取得
 * @param {*} hmTimeCd 訪問時間コード
 * @returns 訪問時間名称
 */
const getHmTimeCdName = (hmTimeCd) => {

  let hmTimeCdName = "";
  switch (hmTimeCd) {
    case "01": hmTimeCdName = "09:00～12:00"
      break;
    case "02": hmTimeCdName = "13:00～15:00"
      break;
    case "03": hmTimeCdName = "15:00～17:00"
      break;
    default:
      hmTimeCdName = "13:00～17:00";
  }
  return hmTimeCdName;
}

/** 登録用データ作成 */
const createData = (state) => {

  // 選択機器一覧
  const selectKikiList = state.storeKikiSelect.selectKiki;

  // メールの受信
  let guidanceAgree = "0";
  if (state.storeContract.guidanceAgree === "true") guidanceAgree = "1";

  return {
    mailAddress: state.storeContract.mailaddress,
    mskmNameSei: state.storeContract.mskmSei,
    mskmNameMei: chgEmptyCharToNull(state.storeContract.mskmMei),
    mskmNameSeiKn: state.storeContract.mskmSeiKn,
    mskmNameMeiKn: chgEmptyCharToNull(state.storeContract.mskmMeiKn),
    mskmPhoneNo: chgEmptyCharToNull(formatTelNumber(state.storeContract.phoneNo)),
    mskmTelNo: chgEmptyCharToNull(formatTelNumber(state.storeContract.telNo)),
    mskmYubinNo: formatYubinNo(state.storeAppointment.yubinNo),
    mskmTokenKj: getTokenName((state.storeAppointment.addressKj).slice(0, 2)),
    mskmAddressCd: state.storeAppointment.addressKj,
    mskmAddressKj: state.storeAppointment.addressKjLabel,
    mskmBantGo: formatBantGo(state.storeContract.bant, state.storeContract.go),
    mskmBldNameKj: chgEmptyCharToNull(state.storeContract.bldNmKj),
    mskmMunebanKn: chgEmptyCharToNull(state.storeContract.muneban),
    mskmHeyabanKn: chgEmptyCharToNull(state.storeContract.heyaban),
    kbuHmDay1: state.storeAppointment.appointmentYmd,
    kbuHmTimeCd1: state.storeAppointment.appointmentHmTimeCd,
    spJk: state.storeContract.contact,
    tmpImage1: formartTempImage(state.storeKikiSelect.tmpImage1),
    tmpImage2: formartTempImage(state.storeKikiSelect.tmpImage2),
    tmpImage3: formartTempImage(state.storeKikiSelect.tmpImage3),
    tmpImageMine1: isEmpty(state.storeKikiSelect.tmpImage1) ? null : "jpeg",
    tmpImageMine2: isEmpty(state.storeKikiSelect.tmpImage2) ? null : "jpeg",
    tmpImageMine3: isEmpty(state.storeKikiSelect.tmpImage3) ? null : "jpeg",
    tntHjnCd: state.storeAppointment.tntHjnCd,
    tntShopCd: state.storeAppointment.tntShopCd,
    tntShopName: state.storeAppointment.tntShopName,
    tntTelNo: state.storeAppointment.tntTelNo,
    newsletter: guidanceAgree,
    recRegUserId: state.storeContract.custNo,
    recUpdUserId: state.storeContract.custNo,
    ktnKnrn: formatCheckBox(selectKikiList, "ktnKnrn"),
    ktnBuiltInKnro: formatCheckBox(selectKikiList, "builtInKnro"),
    ktnGasTable: formatCheckBox(selectKikiList, "gasTable"),
    ktnGasOven: formatCheckBox(selectKikiList, "gasOven"),
    ktnRangeFood: formatCheckBox(selectKikiList, "rangeFood"),
    ktnSuisenJosuiki: formatCheckBox(selectKikiList, "suisenJoski"),
    ktnWashKnsoki: formatCheckBox(selectKikiList, "washKnsoki"),
    bathKnrn: formatCheckBox(selectKikiList, "bathKnrn"),
    bathHeater: formatCheckBox(selectKikiList, "heater"),
    bathKantaKun: formatCheckBox(selectKikiList, "kantaKun"),
    bathSuisen: formatCheckBox(selectKikiList, "bathSuisen"),
    bathShower: formatCheckBox(selectKikiList, "shower"),
    toiletKnrn: formatCheckBox(selectKikiList, "toiletKnrn"),
    toiletSuisen: formatCheckBox(selectKikiList, "toileSuisen"),
    toiletToilet: formatCheckBox(selectKikiList, "toilet"),
    toiletOnsuiSeat: formatCheckBox(selectKikiList, "onsuiSeat"),
    livingKnrn: formatCheckBox(selectKikiList, "livingKnrn"),
    livingNookHywz: formatCheckBox(selectKikiList, "nookHywz"),
    livingGasFanHeater: formatCheckBox(selectKikiList, "gasFaHeater"),
    livingAirCon: formatCheckBox(selectKikiList, "airCon"),
    kytkHtdnEne: formatCheckBox(selectKikiList, "kytkHtdnEne"),
    kytkHtdnEneEnefarm: formatCheckBox(selectKikiList, "enefarm"),
    kytkHtdnEneTaiyoko: formatCheckBox(selectKikiList, "taiyoko"),
    kytkHtdnEneBattery: formatCheckBox(selectKikiList, "battery"),
    kytkHtdnEneGaskyutoki: formatCheckBox(selectKikiList, "gasKyutoki"),
    kytkHtdnEneTes: formatCheckBox(selectKikiList, "tes"),
    safety: formatCheckBox(selectKikiList, "safety"),
    safetyKeihoki: formatCheckBox(selectKikiList, "keihoki"),
    safetyShokaki: formatCheckBox(selectKikiList, "shokaki"),
    other: formatCheckBox(selectKikiList, "other"),
    otherDoor: formatCheckBox(selectKikiList, "door"),
    otherAmido: formatCheckBox(selectKikiList, "amido"),
    otherCloth: formatCheckBox(selectKikiList, "cloth"),
    otherIllumiLed: formatCheckBox(selectKikiList, "illmiLed"),
    otherInput: formatCheckBox(selectKikiList, "otherInput"),
    request: state.storeKikiSelect.request,
    token: sessionStorage["token"]
  }
}

/** base64を登録用に整形 */
const formartTempImage = (base64) => {
  if (base64 !== null && base64 !== "") {
    return base64.replace(/^data:image\/[^;]+;base64,/, "");
  }
}

/**
 * チェック項目を登録用に成形
 * @returns true :チェックあり
 *          false:チェックなし
 */
const formatCheckBox = (selectKikiList, val) => {
  // 機器選択一覧に値が存在する場合
  if (selectKikiList.indexOf(val) !== -1) {
    return true;
  } else {
    return false;
  };
}

export default Conf;